// ============================================================
// Die Wege, die mehr als eine Stelle kennen muss (SPEC §32, E32.1).
//
// Bis §32 lag die Karte auf „/". Seitdem ist „/" für Angemeldete die
// Startseite, und die Karte liegt unter `/karte` — das Projektblatt aber
// behält `/projekte/:id`, weil jeder Link in Mails, im Wochenbrief und in der
// Anwendung selbst darauf zeigt. „Auf der Karte" heißt also ZWEI
// Adressformen, und diese Datei ist die einzige, die das weiß. Kopfleiste,
// Mobilleiste und Suche fragten sonst jede ihre eigene Fassung — und die der
// Suche war schon falsch: sie hielt `/projekte/staende` für die Karte.
// ============================================================
import { matchPath } from "react-router-dom";

/** Die interne Karte. Abgemeldet gibt es sie hier nicht — dann ist „/" die öffentliche (E21). */
export const KARTE = "/karte";

/**
 * Die zwei Abfrageparameter der Karte aus §37 — einmal gelesen, angewandt und
 * mit `replace` wieder entfernt (wie `?neu=1`). `ansicht` hier ist eine
 * Projektansicht (`projekt_ansicht`, 041) und hat mit `/aktuelles?ansicht=forum`
 * nichts zu tun: andere Seite, anderer Weg.
 */
export const KARTE_ANSICHT = "ansicht";
export const KARTE_BEITRAG = "beitrag";

/** `/karte?ansicht=<id>` — die Kachel der Startseite und der kopierte Link (§37.8). */
export function ansichtWeg(id: string): string {
  return `${KARTE}?${KARTE_ANSICHT}=${encodeURIComponent(id)}`;
}

/**
 * Die volle Adresse einer Ansicht zum Weitergeben („Link kopieren“, §37.8 f).
 * `ursprung` nur für die Prüfung; im Browser ist es die eigene Herkunft — ein
 * Link von dev.projekte… führt also auf die Testbühne, einer von projekte… ins
 * Haus. Nach der Anmeldung landet man wieder hier (`anmeldeZiel` behält die
 * Suche).
 */
export function ansichtLink(id: string, ursprung: string = window.location.origin): string {
  return `${ursprung}${ansichtWeg(id)}`;
}

/** `/karte?beitrag=<id>` — „Auf der Karte“ an einem Beitrag mit Ort (§37.10): die Karte fliegt hin und öffnet die Sprechblase. */
export function beitragKartenweg(id: string): string {
  return `${KARTE}?${KARTE_BEITRAG}=${encodeURIComponent(id)}`;
}

/**
 * Die festen Abschnitte unter `/projekte/`, die NICHT die Karte sind. Im
 * Router schlagen sie `:id` von selbst (feste Abschnitte gewinnen); ein
 * `matchPath` weiß davon nichts und hielte „board" für eine Projektkennung.
 * Wer einen weiteren festen Weg unter `/projekte/` anlegt, trägt ihn HIER
 * ein — sonst leuchtet auf ihm der Reiter „Karte", und die Suche glaubt,
 * sie stünde schon auf der Karte, und navigiert nicht hin.
 */
// §52.7 a: „vorschau" — sonst hielte die Anwendung `/projekte/vorschau` für ein Projekt.
const FESTE = new Set(["board", "staende", "vorschau"]);

/** Zeigt dieser Pfad die interne Karte? `/karte` und `/projekte/:id`, ohne die festen Abschnitte. */
export function istKartenweg(pfad: string): boolean {
  if (matchPath(KARTE, pfad)) return true;
  const m = matchPath("/projekte/:id", pfad);
  // Kleingeschrieben verglichen: der Router passt ohne Rücksicht auf die
  // Schreibung, `/projekte/Board` ist also das Board und nicht die Karte.
  return !!m && !FESTE.has((m.params.id ?? "").toLowerCase());
}

/**
 * Leuchtet ein Reiter der Kopf- oder Mobilleiste?
 *
 * Auf einem Kartenweg leuchtet NUR „Karte". Ohne diese Regel stünde auf
 * `/projekte/:id` der Reiter „Projekte" (der ohne `end` jeden Pfad darunter
 * für sich nimmt) — während man auf die Karte schaut. Sonst gilt dieselbe
 * Regel wie bei `NavLink`: Präfix an einer Abschnittsgrenze, mit `genau` nur
 * der Pfad selbst.
 *
 * Warum die Leisten `Link` statt `NavLink` nehmen: `NavLink` setzt
 * `aria-current` nach SEINER Rechnung, und eine eigene lässt es nicht zu —
 * der Vorleser hätte „Projekte" als aktuelle Seite angesagt, während das
 * Bild „Karte" hervorhebt.
 */
export function reiterAn(zu: string, pfad: string, genau = false): boolean {
  if (zu === KARTE) return istKartenweg(pfad);
  if (istKartenweg(pfad)) return false;
  // §51.8 f: „Gelernt" hat keinen eigenen Reiter (die Leiste ist voll) und wird aus
  // Aktuelles erreicht, mit „‹ Aktuelles" im Kopf — also leuchtet dort „Aktuelles".
  // Derselbe Pfad wie `GELERNT_PFAD` (api/abschluss.ts; der Test prüft die Gleichheit).
  if (zu === "/aktuelles" && matchPath({ path: GELERNT, end: false }, pfad) !== null) return true;
  return matchPath({ path: zu, end: genau }, pfad) !== null;
}

/** Die Seite „Gelernt" (§51.8 f) — gleich `GELERNT_PFAD`, hier ohne Import aus `api/`. */
const GELERNT = "/gelernt";

/** Läuft das Vorladen schon (oder ist es fertig)? Nach einem Fehlschlag wieder `null`. */
let vorladen: Promise<unknown> | null = null;

/**
 * Das Kartenbündel vorab holen — MapLibre allein wiegt über 800 kB.
 *
 * Seit die Startseite auf „/" steht, lädt sie die Karte nicht mehr mit; der
 * erste Klick auf „Karte" zöge das ganze Bündel erst dann. Also holt die
 * Startseite es im Leerlauf, und die Leisten beim Zeigen auf den Reiter.
 * Derselbe Modulpfad wie das `lazy` in `App.tsx`: der Bündler macht daraus
 * EIN Stück, das Vorladen füllt also genau den Zwischenspeicher, aus dem die
 * Karte gleich liest.
 *
 * Einmal genügt. Scheitert es (kein Netz), darf der nächste Anlass es noch
 * einmal versuchen — ein gemerkter Fehlschlag hielte das Vorladen für immer
 * aus, und den eigentlichen Klick versucht `lazy` ohnehin selbst.
 */
export function karteVorladen(): void {
  if (vorladen) return;
  vorladen = import("../screens/karte/Karte").catch(() => {
    vorladen = null;
  });
}

/**
 * Wohin nach der Anmeldung? `von` setzen die Wächter in `App.tsx`
 * (Pfad + Suche, damit `?reiter=team` aus einer Mail mitreist) — gelesen
 * wird es erst seit §32. Es kommt aus `history.state` und ist damit
 * Eingabe: wer einen Verweis baut, kann dort hineinschreiben, was er will.
 *
 * Durch kommt nur ein Pfad DIESER Anwendung:
 *  - er beginnt mit „/", aber nicht mit „//" (das wäre ein anderer Rechner,
 *    `//fremd.de`);
 *  - kein Backslash — Browser lesen `/\fremd.de` wie `//fremd.de`;
 *  - keine Steuerzeichen — der Adressleser wirft Tab und Zeilenumbruch
 *    stillschweigend heraus, aus `/\t/fremd.de` würde `//fremd.de`;
 *  - nicht die Anmeldung selbst (in jeder Schreibung, auch kodiert), sonst
 *    liefe man nach dem Code wieder auf die Anmeldung.
 * Alles andere führt auf „/".
 */
export function anmeldeZiel(von: unknown): string {
  if (typeof von !== "string") return "/";
  if (!von.startsWith("/") || von.startsWith("//")) return "/";
  // eslint-disable-next-line no-control-regex -- genau die Steuerzeichen sind gemeint
  if (/[\\\u0000-\u001f\u007f]/.test(von)) return "/";
  const weg = von.split(/[?#]/, 1)[0] ?? "";
  let gelesen: string;
  try {
    gelesen = decodeURIComponent(weg).toLowerCase();
  } catch {
    // Eine kaputte Kodierung (`%E0%A4%A`) kommt aus keinem unserer Links.
    return "/";
  }
  if (gelesen.startsWith("//") || gelesen.includes("\\") || gelesen.startsWith("/anmelden")) return "/";
  return von;
}
